// src/components/PayrollExportPanel.jsx
import React, { useMemo, useState } from "react";
import { calculatePayForEmployee } from "../utils/calculatePayForEmployee";

/**
 * Steg-baserad panel för löneexport med fast header/stepper/footer.
 * Innehållsdelen är skrollbar.
 *
 * För att använda calculatePayForEmployee, skicka in:
 *  - employees           : Employee[] (de du vill visa i panelen)
 *  - draftSchedule       : Shift[]
 *  - selectedMonth       : number (1-12)
 *  - selectedYear        : number (t.ex. 2025)
 *  - selectedTimeSchema  : string|null (timeSchema._id eller null för alla)
 *  - payRates            : Pay[]        (= allPays)
 *  - vacationPay         : VacationPay
 *  - overtimeRules       : OvertimeRule[]
 *  - obRules             : ObRule[]     (= obs)
 *  - absenceRules        : AbsenceRule[]
 *
 * Om ovan saknas används fallback-prop: employeeSummaries = [{id, name, hours}]
 */
export default function PayrollExportPanel({
  isOpen,
  onClose,
  period,
  payrollSystem,
  setPayrollSystem,
  totals,
  mapping,
  setMapping,
  onGeneratePaxml,

  // === Nya props för 100% korrekt beräkning ===
  employees,
  draftSchedule,
  selectedMonth,
  selectedYear,
  selectedTimeSchema,
  payRates,
  vacationPay,
  overtimeRules,
  obRules,
  absenceRules,

  // Fallback (om man inte vill/kan skicka in allt ovan)
  employeeSummaries = [],
}) {
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState("");
  const [sortAsc, setSortAsc] = useState(false);

  const missing = useMemo(() => getMissing(mapping), [mapping]);
  const prettyPeriod = `${fmt(period.from)} – ${fmt(period.to)}`;
  const canGoNextFrom1 = !!payrollSystem;
  const canGoNextFrom2 = missing.length === 0 && !!payrollSystem;

  // === shift-filter helper (samma skydd som i Payroll.js) ===
  const isShiftCompleteForPayroll = (shift) => !!(
    shift &&
    shift.date &&
    typeof shift.startTime === "string" && shift.startTime.includes(":") &&
    typeof shift.endTime === "string" && shift.endTime.includes(":") &&
    Array.isArray(shift.employees) &&
    shift.employees.some(e => !!e?.employeeId)
  );

  // === Kan vi räkna "på riktigt"? ===
  const canCalculate =
    Array.isArray(employees) &&
    Array.isArray(draftSchedule) &&
    typeof selectedMonth === "number" &&
    typeof selectedYear === "number" &&
    Array.isArray(payRates) &&
    Array.isArray(overtimeRules) &&
    Array.isArray(obRules) &&
    Array.isArray(absenceRules);

  // === Beräkna rader med calculatePayForEmployee ===
  const calcRows = useMemo(() => {
    if (!canCalculate) return null;

    return employees.map(emp => {
      const empShifts = draftSchedule.filter(shift => {
        if (!isShiftCompleteForPayroll(shift)) return false;
        const d = new Date(shift.date);
        const matchesMonth    = d.getMonth() + 1 === selectedMonth;
        const matchesYear     = d.getFullYear() === selectedYear;
        const matchesSchema   = !selectedTimeSchema || shift.timeSchema === selectedTimeSchema;
        const matchesEmployee = shift.employees.some(e => e.employeeId === emp._id);
        return matchesMonth && matchesYear && matchesSchema && matchesEmployee;
      });

      const { totalHours } = calculatePayForEmployee({
        employee: emp,
        shifts: empShifts,
        payRates,
        vacationPay,
        overtimeRules,
        obRules,
        absenceRules
      });

      return {
        id: emp._id,
        name: emp.name,
        hours: Number.isFinite(totalHours) ? totalHours : 0,
      };
    });
  }, [
    canCalculate,
    employees,
    draftSchedule,
    selectedMonth,
    selectedYear,
    selectedTimeSchema,
    payRates,
    vacationPay,
    overtimeRules,
    obRules,
    absenceRules
  ]);

  // === Filter + sortering för tabellen (använder calcRows om möjligt) ===
  const filteredRows = useMemo(() => {
    const base = (calcRows ?? employeeSummaries) || [];
    const q = (search || "").toLowerCase();
    const filtered = base.filter(r => (r.name || "").toLowerCase().includes(q));
    return filtered.sort((a, b) => (sortAsc ? a.hours - b.hours : b.hours - a.hours));
  }, [calcRows, employeeSummaries, search, sortAsc]);

  if (!isOpen) return null;

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header (fast) */}
        <div style={headerStyle}>
          <div style={headerContent}>
            <div style={titleContainer}>
            <h1
            style={{
                fontSize: "22px",
                fontWeight: 700,
                color: "#1e293b",          // mörkare, modern textfärg
                marginBottom: "6px",       // bättre avstånd
                letterSpacing: "-0.2px",   // stramare, proffsigare
            }}
            >
            Exportera löneunderlag
            </h1>
            <div
            style={{
                fontSize: "15px",
                color: "#64748b",          // mer subtil gråblå
                lineHeight: "1.4",         // luftigare radavstånd
                marginBottom: "16px",      // skapar visuell andning
                maxWidth: "600px",         // begränsa bredd för bättre läsbarhet
            }}
            >
            Generera en fil som kan importeras direkt i ert lönesystem för utbetalning och bokföring av löner.
            </div>

            </div>
            <div style={badge}>{prettyPeriod}</div>
          </div>
          <button style={closeBtn} onClick={onClose} aria-label="Stäng">
            ×
          </button>
        </div>

        {/* Stepper (fast) */}
        <Stepper step={step} />

        {/* Innehåll (skrollbar) */}
        <div style={contentWrap}>
          <div style={contentInner}>
            {step === 1 && (
              <StepSummary
                prettyPeriod={prettyPeriod}
                payrollSystem={payrollSystem}
                setPayrollSystem={setPayrollSystem}
                totals={totals}
                rows={filteredRows}
                search={search}
                setSearch={setSearch}
                sortAsc={sortAsc}
                setSortAsc={setSortAsc}
              />
            )}

            {step === 2 && (
              <StepMappings
                payrollSystem={payrollSystem}
                mapping={mapping}
                setMapping={setMapping}
                missing={missing}
              />
            )}

            {step === 3 && (
              <StepReview
                prettyPeriod={prettyPeriod}
                payrollSystem={payrollSystem}
                totals={totals}
                missing={missing}
                mapping={mapping}
              />
            )}
          </div>
        </div>

        {/* Footer (fast) */}
        <div style={footerBar}>
          <div style={footerStatus}>
            {step === 1 && !payrollSystem && (
              <div style={statusText}>
                Välj lönesystem för att fortsätta
              </div>
            )}
            {step === 2 && missing.length > 0 && (
              <div style={statusText}>
                Fyll i {missing.length} saknad{missing.length > 1 ? 'a' : ''} kod{missing.length > 1 ? 'er' : ''} för att fortsätta
              </div>
            )}
          </div>

          <div style={footerActions}>
            <button style={secondaryBtn} onClick={onClose}>
              Avbryt
            </button>

            {step > 1 && (
              <button style={ghostBtn} onClick={() => setStep(step - 1)}>
                Tillbaka
              </button>
            )}

            {step < 3 && (
              <button
                onClick={() => setStep(step + 1)}
                style={{
                  ...primaryBtn,
                  ...((step === 1 && !canGoNextFrom1) || (step === 2 && !canGoNextFrom2)
                    ? disabledPrimary
                    : {}
                  ),
                }}
                disabled={(step === 1 && !canGoNextFrom1) || (step === 2 && !canGoNextFrom2)}
              >
                Nästa
              </button>
            )}

            {step === 3 && (
              <button
                style={primaryBtn}
                onClick={onGeneratePaxml}
              >
                Ladda ned PAXml
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======================= STEPPER ======================= */
function Stepper({ step }) {
  const steps = [
    { id: 1, label: "Översikt" },
    { id: 2, label: "Koder" },
    { id: 3, label: "Exportera" },
  ];
  
  return (
    <div style={stepperWrap}>
      {steps.map((s, i) => {
        const active = step === s.id;
        const done = step > s.id;
        return (
          <div style={stepItem} key={s.id}>
            <div style={stepContent}>
              <div style={{
                ...stepBullet,
                background: done ? successColor
                  : (active ? primaryColor : neutral200),
                color: done || active ? "#fff" : neutral600,
              }}>
                {done ? "✓" : s.id}
              </div>
              <div style={{
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                color: active ? neutral900 : neutral600
              }}>
                {s.label}
              </div>
            </div>
            {i < steps.length - 1 && <div style={stepLine} />}
          </div>
        );
      })}
    </div>
  );
}

/* ======================= STEG 1 ======================= */
function StepSummary({
  prettyPeriod,
  payrollSystem,
  setPayrollSystem,
  totals,
  rows,
  search,
  setSearch,
  sortAsc,
  setSortAsc
}) {
  return (
    <>
      {/* Översiktskort */}
      <div style={cardStyle}>
        <div style={cardHeader}>
          <div style={cardTitle}>Översikt</div>
        </div>
        <div style={summaryGrid}>
          <SummaryItem 
            label="Period" 
            value={prettyPeriod}
          />
          <SummaryItem 
            label="Anställda" 
            value={totals?.employeesCount ?? 0}
          />
          <SummaryItem 
            label="Totala timmar" 
            value={(totals?.hours ?? 0).toFixed(2)}
          />
          <div>
            <div style={inputLabel}>Lönesystem</div>
            <select
              value={payrollSystem || ""}
              onChange={(e) => setPayrollSystem(e.target.value)}
              style={selectStyle}
            >
              <option value="">Välj lönesystem...</option>
              <option value="fortnox">Fortnox</option>
              <option value="visma_tlu">Visma Lön 300/600 (TLU)</option>
              <option value="kontek">Kontek</option>
              <option value="bl_lon">BL Lön</option>
              <option value="crona">Crona Lön</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabell: Anställda & timmar (beräknat) */}
      <div style={cardStyle}>
        <div style={cardHeader}>
          <div style={cardTitle}>Anställda & timmar</div>
        </div>

        <div style={tableControls}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök anställda..."
            style={searchInput}
          />
          <button
            style={sortBtn}
            onClick={() => setSortAsc(!sortAsc)}
          >
            Timmar {sortAsc ? "↑" : "↓"}
          </button>
        </div>

        <div style={tableContainer}>
          {rows.length === 0 ? (
            <div style={emptyState}>
              Inga anställda matchar sökningen
            </div>
          ) : (
            <div style={tableWrap}>
              <div style={tableHeader}>
                <div style={{ ...th, flex: 3 }}>Namn</div>
                <div style={{ ...th, flex: 1, textAlign: "right" }}>Timmar</div>
              </div>
              <div style={tableBody}>
                {rows.map((r) => (
                  <div key={r.id} style={tr}>
                    <div style={{ ...td, flex: 3 }}>{r.name}</div>
                    <div style={{ ...td, flex: 1, textAlign: "right" }}>
                      {(r.hours ?? 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ======================= STEG 2 ======================= */
function StepMappings({ payrollSystem, mapping, setMapping, missing }) {
  if (!payrollSystem) {
    return (
      <div style={hintBox}>
        Välj lönesystem i föregående steg för att fylla i koder.
      </div>
    );
  }

  return (
    <>
      <div style={cardStyle}>
        <div style={cardHeader}>
          <div style={cardTitle}>Löneartskoder för {humanSystem(payrollSystem)}</div>
        </div>

        <MappingSection
          title="Timlön"
          rows={[{ name: "Grundlön", code: mapping.base }]}
          onChange={(idx, code) => setMapping({ ...mapping, base: code })}
        />
        
        <MappingSection
          title="OB-tillägg"
          rows={mapping.ob || []}
          onChange={(idx, code) => setMapping({ ...mapping, ob: updateAt(mapping.ob || [], idx, code) })}
        />
        
        <MappingSection
          title="Övertid"
          rows={mapping.ot || []}
          onChange={(idx, code) => setMapping({ ...mapping, ot: updateAt(mapping.ot || [], idx, code) })}
        />
        
        <MappingSection
          title="Frånvaro"
          rows={mapping.absence || []}
          onChange={(idx, code) => setMapping({ ...mapping, absence: updateAt(mapping.absence || [], idx, code) })}
        />
        
        <MappingSection
          title="Semesterersättning"
          rows={[{ name: "Semesterersättning", code: mapping.vacation ?? "" }]}
          onChange={(idx, code) => setMapping({ ...mapping, vacation: code })}
        />

        {missing.length > 0 && (
          <div style={missingText}>
            Saknade koder: {missing.join(", ")}
          </div>
        )}
      </div>
    </>
  );
}


/* ======================= STEG 3 (tillbaka till tidigare stil) ======================= */
function StepReview({ prettyPeriod, payrollSystem, totals, missing, mapping }) {
  const groups = [
    { title: "Timlön", items: [{ label: "Grundlön", code: mapping.base }] },
    { title: "Semester", items: [{ label: "Semesterersättning", code: mapping.vacation }] },
    {
      title: "OB-tillägg",
      items: (mapping.ob || []).map((r, i) => ({ label: r.name || `OB #${i + 1}`, code: r.code })),
    },
    {
      title: "Övertid",
      items: (mapping.ot || []).map((r, i) => ({ label: r.name || `Övertid #${i + 1}`, code: r.code })),
    },
    {
      title: "Frånvaro",
      items: (mapping.absence || []).map((r, i) => ({ label: r.name || `Frånvaro #${i + 1}`, code: r.code })),
    },
  ];

  return (
    <>
      {/* Översta sammanfattningskortet – som innan */}
      <div style={cardStyle}>
        <div style={cardHeader}>
          <div style={cardTitle}>Granskning</div>
        </div>
        <div style={reviewGrid}>
          <ReviewItem label="Period" value={prettyPeriod} />
          <ReviewItem label="Lönesystem" value={humanSystem(payrollSystem)} />
          <ReviewItem label="Anställda" value={totals?.employeesCount ?? 0} />
          <ReviewItem label="Timmar" value={(totals?.hours ?? 0).toFixed(2)} />
        </div>
      </div>

      {/* Alla löneartskoder – enkel, luftig layout i grid */}
      <div style={cardStyle}>
        <div style={cardHeader}>
          <div style={cardTitle}>Löneartskoder</div>
        </div>

        <div style={{ padding: "16px 20px", display: "grid", gap: 16 }}>
          {groups.map((g, gi) => (
            <CodesGroup key={gi} title={g.title} items={g.items} />
          ))}
        </div>

        {missing.length > 0 && (
          <div style={warningAlert}>
            Varning: {missing.length} kod{missing.length > 1 ? "er" : ""} saknas
          </div>
        )}
      </div>
    </>
  );
}

/* Grupp-rubrik + ditt befintliga tvåkolumns-grid med MiniCode-kort */
function CodesGroup({ title, items }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 8 }}>
        {title}
      </div>
      {(!items || items.length === 0) ? (
        <div style={{ fontSize: 13, color: "#64748b", fontStyle: "italic" }}>Inga rader</div>
      ) : (
        <div style={codesGrid}>
          {items.map((it, idx) => (
            <MiniCode key={`${title}-${it.label || idx}`} label={it.label} code={it.code} />
          ))}
        </div>
      )}
    </div>
  );
}





/* ====== lägg till/ersätt denna style ====== */
const codesGridFull = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: 12
};


/* ====== underkomponenter ====== */
function SummaryItem({ label, value }) {
  return (
    <div>
      <div style={summaryLabel}>{label}</div>
      <div style={summaryValue}>{value}</div>
    </div>
  );
}

function MappingSection({ title, rows, onChange }) {
  return (
    <div style={mappingSection}>
      <div style={mappingTitle}>{title}</div>
      <div style={mappingRows}>
        {rows.length === 0 ? (
          <div style={noRules}>
            Inga regler hittades
          </div>
        ) : rows.map((r, idx) => (
          <div key={idx} style={mapRow}>
            <div style={mapName}>{r.name}</div>
            <input
              value={r.code || ""}
              onChange={(e) => onChange(idx, e.target.value)}
              placeholder="Löneartkod"
              style={codeInput(r.code)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div>
      <div style={reviewLabel}>{label}</div>
      <div style={reviewValue}>{value || "—"}</div>
    </div>
  );
}

function MiniCode({ label, code }) {
  return (
    <div style={codeCard}>
      <div style={codeLabel}>{label}</div>
      <div style={codeValue}>{code || "—"}</div>
    </div>
  );
}

/* ====== styles ====== */
const primaryColor = "#3b82f6";
const successColor = "#10b981";
const errorColor = "#ef4444";
const neutral50 = "#f8fafc";
const neutral100 = "#f1f5f9";
const neutral200 = "#e2e8f0";
const neutral300 = "#cbd5e1";
const neutral400 = "#94a3b8";
const neutral500 = "#64748b";
const neutral600 = "#475569";
const neutral700 = "#334155";
const neutral800 = "#1e293b";
const neutral900 = "#0f172a";

const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.40)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
  padding: 16,
};

const panelStyle = {
  width: "min(1000px, 95vw)",
  height: "min(800px, 90vh)",
  background: "#ffffff",
  borderRadius: 12,
  boxShadow: "0 20px 60px rgba(0,0,0,.25)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
};

const headerStyle = {
  padding: "20px 24px",
  borderBottom: `1px solid ${neutral200}`,
  background: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start"
};

const headerContent = {
  display: "flex",
  alignItems: "flex-start",
  gap: 16,
  flex: 1
};

const titleContainer = {
  flex: 1
};

const title = {
  fontWeight: 700,
  fontSize: 20,
  color: neutral900,
  margin: 0,
  marginBottom: 4,
};

const subtitle = {
  fontSize: 14,
  color: neutral600,
};

const badge = {
  fontSize: 13,
  fontWeight: 600,
  color: neutral700,
  background: neutral100,
  border: `1px solid ${neutral300}`,
  borderRadius: 8,
  padding: "6px 12px",
  whiteSpace: "nowrap"
};

const closeBtn = {
  background: "none",
  border: "none",
  fontSize: 24,
  color: neutral500,
  cursor: "pointer",
  padding: 0,
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const contentWrap = {
  flex: 1,
  overflowY: "auto",
  background: neutral50
};

const contentInner = {
  padding: "16px 0"
};

const stepperWrap = {
  display: "flex",
  alignItems: "center",
  padding: "16px 24px",
  borderBottom: `1px solid ${neutral200}`,
  background: "#fff"
};

const stepItem = { 
  display: "flex", 
  alignItems: "center", 
  flex: 1, 
  position: "relative" 
};

const stepContent = {
  display: "flex",
  alignItems: "center",
  gap: 8
};

const stepBullet = {
  width: 24, 
  height: 24, 
  borderRadius: "50%",
  display: "flex", 
  alignItems: "center", 
  justifyContent: "center",
  fontSize: 12, 
  fontWeight: 600,
  flexShrink: 0
};

const stepLine = {
  position: "absolute", 
  right: -8, 
  top: "50%", 
  transform: "translateY(-50%)",
  height: 1, 
  width: "calc(100% - 24px - 8px)", 
  background: neutral300, 
  zIndex: -1
};

const cardStyle = {
  border: `1px solid ${neutral200}`,
  borderRadius: 8,
  overflow: "hidden",
  margin: "0 24px 16px 24px",
  background: "#fff"
};

const cardHeader = {
  padding: "16px 20px",
  borderBottom: `1px solid ${neutral200}`,
  background: neutral50
};

const cardTitle = {
  fontWeight: 600,
  fontSize: 16,
  color: neutral900,
};

const summaryGrid = { 
  display: "grid", 
  gridTemplateColumns: "1fr 1fr 1fr 1fr", 
  gap: 20, 
  padding: "20px" 
};

const summaryLabel = { 
  fontSize: 14, 
  color: neutral600, 
  marginBottom: 4 
};

const summaryValue = { 
  fontSize: 16, 
  fontWeight: 600, 
  color: neutral900 
};

const inputLabel = {
  fontSize: 14,
  color: neutral600,
  marginBottom: 8,
  fontWeight: 500
};

const tableControls = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 20px",
  borderBottom: `1px solid ${neutral200}`,
  gap: 12
};

const searchInput = {
  padding: "8px 12px",
  borderRadius: 6,
  border: `1px solid ${neutral300}`,
  background: "#fff",
  fontSize: 14,
  flex: 1,
  maxWidth: 300
};

const sortBtn = {
  padding: "8px 12px",
  borderRadius: 6,
  border: `1px solid ${neutral300}`,
  background: "#fff",
  fontSize: 13,
  cursor: "pointer",
  color: neutral700,
  whiteSpace: "nowrap"
};

const tableContainer = {
  padding: "8px 0 16px 0"
};

const emptyState = {
  padding: "32px 20px",
  textAlign: "center",
  color: neutral500,
  fontSize: 14
};

const tableWrap = { 
  margin: "0 20px",
  border: `1px solid ${neutral200}`, 
  borderRadius: 6, 
  overflow: "hidden"
};

const tableHeader = {
  display: "flex", 
  alignItems: "center", 
  padding: "12px 16px", 
  background: neutral50,
  borderBottom: `1px solid ${neutral200}`
};

const th = { 
  fontSize: 13, 
  fontWeight: 600, 
  color: neutral700,
};

const tableBody = {
  maxHeight: 300,
  overflowY: "auto"
};

const tr = {
  display: "flex", 
  alignItems: "center", 
  padding: "12px 16px",
  borderTop: `1px solid ${neutral100}`,
};

const td = { 
  fontSize: 14, 
  color: neutral800 
};

const mappingSection = {
  padding: "16px 20px",
  borderTop: `1px solid ${neutral200}`
};

const mappingTitle = {
  fontWeight: 600,
  fontSize: 14,
  color: neutral800,
  marginBottom: 12
};

const mappingRows = {
  display: "flex",
  flexDirection: "column",
  gap: 12
};

const noRules = {
  padding: "8px 0",
  color: neutral500,
  fontSize: 13,
  fontStyle: "italic"
};

const mapRow = {
  display: "grid", 
  gridTemplateColumns: "1fr 200px", 
  gap: 16, 
  alignItems: "center"
};

const mapName = {
  fontSize: 14,
  color: neutral700,
};

const reviewGrid = { 
  display: "grid", 
  gridTemplateColumns: "1fr 1fr 1fr 1fr", 
  gap: 20, 
  padding: "20px" 
};

const reviewLabel = { 
  fontSize: 14, 
  color: neutral600, 
  marginBottom: 4 
};

const reviewValue = { 
  fontSize: 16, 
  fontWeight: 600, 
  color: neutral900 
};

const codesGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  padding: "20px"
};

const codeCard = {
  border: `1px solid ${neutral300}`,
  borderRadius: 6,
  padding: 16,
  background: "#fff"
};

const codeLabel = {
  fontSize: 13,
  fontWeight: 500,
  color: neutral700,
  marginBottom: 8
};

const codeValue = {
  fontSize: 16,
  fontWeight: 600,
  color: neutral900,
};

const hintBox = {
  border: `1px dashed ${neutral300}`,
  background: neutral50,
  borderRadius: 8,
  padding: 20,
  color: neutral700,
  fontSize: 14,
  margin: "0 24px 16px 24px",
  textAlign: "center"
};

const missingText = {
  padding: "12px 20px",
  color: errorColor,
  fontSize: 13,
  borderTop: `1px solid ${neutral200}`
};

const warningAlert = {
  padding: "12px 20px",
  color: errorColor,
  fontSize: 13,
  borderTop: `1px solid ${neutral200}`,
  background: `${errorColor}08`
};

const footerBar = {
  padding: "16px 24px",
  borderTop: `1px solid ${neutral200}`,
  background: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16
};

const footerStatus = {
  flex: 1
};

const footerActions = {
  display: "flex",
  gap: 12,
  alignItems: "center"
};

const statusText = {
  fontSize: 14,
  color: neutral600,
};

const selectStyle = {
  padding: "8px 12px",
  borderRadius: 6,
  border: `1px solid ${neutral300}`,
  background: "#fff",
  fontSize: 14,
  width: "100%"
};

const primaryBtn = {
  padding: "10px 20px",
  borderRadius: 6,
  border: "none",
  background: primaryColor,
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const disabledPrimary = {
  background: neutral400,
  cursor: "not-allowed",
};

const secondaryBtn = {
  padding: "10px 16px",
  borderRadius: 6,
  border: `1px solid ${neutral300}`,
  background: "#fff",
  fontSize: 14,
  cursor: "pointer",
  color: neutral700,
};

const ghostBtn = {
  padding: "10px 16px",
  borderRadius: 6,
  border: `1px solid ${neutral300}`,
  background: "transparent",
  fontSize: 14,
  cursor: "pointer",
  color: neutral700,
};

const codeInput = (has) => ({
  padding: "8px 12px",
  borderRadius: 6,
  border: `1px solid ${has ? neutral300 : errorColor}`,
  background: "#fff",
  fontSize: 14,
  width: "100%"
});

/* ====== helpers ====== */
function updateAt(arr, idx, code) { return arr.map((x, i) => i === idx ? { ...x, code } : x); }
function getMissing(m) {
  const miss = [];
  if (!m?.base) miss.push("Timlön");
  (m?.ob || []).forEach(r => { if (!r.code) miss.push(r.name); });
  (m?.ot || []).forEach(r => { if (!r.code) miss.push(r.name); });
  (m?.absence || []).forEach(r => { if (!r.code) miss.push(r.name); });
  if (m?.vacation !== undefined && !m.vacation) miss.push("Semesterersättning");
  return miss;
}
function fmt(d){ return new Date(d).toLocaleDateString("sv-SE"); }
function humanSystem(code) {
  switch (code) {
    case "fortnox": return "Fortnox";
    case "visma_tlu": return "Visma Lön 300/600 (TLU)";
    case "kontek": return "Kontek";
    case "bl_lon": return "BL Lön";
    case "crona": return "Crona Lön";
    default: return "—";
  }
}